import React, { useState, useEffect } from 'react';

const WidgetBuilder = ({ 
  currentUser, 
  onSave, 
  editingWidgetId = null
}) => {
  const [widgetConfig, setWidgetConfig] = useState({
    botName: 'AI Assistant',
    companyName: 'Your Company',
    botLogo: 'https://www.defindly.com/wp-content/uploads/2025/09/Logo-Blue-Background.png',
    primaryColor: '#06ADED',
    accentColor: '#38BFEF',
    position: 'bottom-right',
    greeting: 'Hi! How can I help you today?',
    placeholder: 'Type your message...',
    enableAttachments: true,
    enableAudio: true,
    enableSuggestions: true,
    enableWhatsApp: false,
    whatsAppNumber: '',
    suggestions: [
      "How can you help me?",
      "What services do you offer?",
      "How do I get started?"
    ]
  });

  const [widgetId, setWidgetId] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editingWidgetId) {
      loadWidget(editingWidgetId);
    } else if (!widgetId) {
      const newId = generateWidgetId();
      setWidgetId(newId);
    }
  }, [editingWidgetId]);

  const loadWidget = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://app.defindly.com/api/get-widget.php?id=${id}`);
      const data = await response.json();
      
      if (data.success && data.config) {
        setWidgetConfig({...widgetConfig, ...data.config});
        setWidgetId(id);
        setIsSaved(true);
      } else {
        alert('Widget not found');
      }
    } catch (error) {
      console.error('Error loading widget:', error);
      alert('Failed to load widget');
    }
    setIsLoading(false);
  };

  const generateWidgetId = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `wgt_${timestamp}${randomStr}`;
  };

  const updateConfig = (key, value) => {
    setWidgetConfig(prev => ({
      ...prev,
      [key]: value
    }));
    setIsSaved(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateConfig('botLogo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const updateSuggestion = (index, value) => {
    setWidgetConfig(prev => ({
      ...prev,
      suggestions: prev.suggestions.map((s, i) => i === index ? value : s)
    }));
    setIsSaved(false);
  };

  const addSuggestion = () => {
    if (widgetConfig.suggestions.length < 6) {
      setWidgetConfig(prev => ({
        ...prev,
        suggestions: [...prev.suggestions, 'New suggestion']
      }));
      setIsSaved(false);
    }
  };

  const removeSuggestion = (index) => {
    setWidgetConfig(prev => ({
      ...prev,
      suggestions: prev.suggestions.filter((_, i) => i !== index)
    }));
    setIsSaved(false);
  };

  const saveWidget = async () => {
    setIsSaving(true);

    const widgetData = {
      id: widgetId,
      config: widgetConfig,
      userId: currentUser?.id,
      userEmail: currentUser?.email,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      const response = await fetch('https://app.defindly.com/api/save-widget.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(widgetData)
      });

      const result = await response.json();

      if (result.success) {
        setIsSaved(true);
        setIsSaving(false);
        
        if (onSave) {
          onSave(widgetData);
        }
        
        alert('Widget saved successfully!');
      } else {
        throw new Error(result.error || 'Failed to save widget');
      }
    } catch (error) {
      console.error('Error saving widget:', error);
      setIsSaving(false);
      alert('Failed to save widget. Please try again.');
    }
  };

  const copyEmbedCode = () => {
    const embedCode = `<script src="https://app.defindly.com/embed/widget-embed.js" data-widget-id="${widgetId}"></script>`;
    navigator.clipboard.writeText(embedCode);
    alert('Embed code copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-2xl mb-4">Loading widget...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="page-title">
              {editingWidgetId ? 'Edit Widget' : 'Create Widget'}
            </h1>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customize Your Widget</h2>
          
          <div className="space-y-8 max-w-3xl">
            <div>
              <h3 className="section-title">Basic Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Bot Name</label>
                  <input
                    type="text"
                    value={widgetConfig.botName}
                    onChange={(e) => updateConfig('botName', e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div>
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    value={widgetConfig.companyName}
                    onChange={(e) => updateConfig('companyName', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Bot Logo</label>
                  <div className="flex items-center gap-4">
                    <img 
                      src={widgetConfig.botLogo} 
                      alt="Bot Logo" 
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #e5e7eb'
                      }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="form-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Upload a square image for best results (recommended: 200x200px)
                  </p>
                </div>

                <div>
                  <label className="form-label">Greeting Message</label>
                  <textarea
                    value={widgetConfig.greeting}
                    onChange={(e) => updateConfig('greeting', e.target.value)}
                    rows={3}
                    className="form-textarea"
                  />
                </div>

                <div>
                  <label className="form-label">Input Placeholder</label>
                  <input
                    type="text"
                    value={widgetConfig.placeholder}
                    onChange={(e) => updateConfig('placeholder', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <div style={{ paddingTop: '3rem' }}>
              <h3 className="section-title">Appearance</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Primary Color</label>
                    <input
                      type="color"
                      value={widgetConfig.primaryColor}
                      onChange={(e) => updateConfig('primaryColor', e.target.value)}
                      className="w-full h-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="form-label">Accent Color</label>
                    <input
                      type="color"
                      value={widgetConfig.accentColor}
                      onChange={(e) => updateConfig('accentColor', e.target.value)}
                      className="w-full h-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Position</label>
                  <select
                    value={widgetConfig.position}
                    onChange={(e) => updateConfig('position', e.target.value)}
                    className="form-select"
                  >
                    <option value="bottom-right">Bottom Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="top-left">Top Left</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={{ paddingTop: '3rem' }}>
              <h3 className="section-title">Features</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={widgetConfig.enableAttachments}
                    onChange={(e) => updateConfig('enableAttachments', e.target.checked)}
                    style={{ accentColor: '#06ADED' }}
                  />
                  <span>Enable file uploads</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={widgetConfig.enableAudio}
                    onChange={(e) => updateConfig('enableAudio', e.target.checked)}
                    style={{ accentColor: '#06ADED' }}
                  />
                  <span>Enable voice messages</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={widgetConfig.enableSuggestions}
                    onChange={(e) => updateConfig('enableSuggestions', e.target.checked)}
                    style={{ accentColor: '#06ADED' }}
                  />
                  <span>Show quick suggestions</span>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={widgetConfig.enableWhatsApp}
                    onChange={(e) => updateConfig('enableWhatsApp', e.target.checked)}
                    style={{ accentColor: '#06ADED' }}
                  />
                  <span>Enable WhatsApp integration</span>
                </label>
              </div>
            </div>

            {widgetConfig.enableWhatsApp && (
              <div style={{ paddingTop: '1.5rem' }}>
                <h3 className="section-title">WhatsApp Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="form-label">WhatsApp Phone Number</label>
                    <input
                      type="text"
                      value={widgetConfig.whatsAppNumber}
                      onChange={(e) => updateConfig('whatsAppNumber', e.target.value)}
                      placeholder="e.g., +1234567890"
                      className="form-input"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Include country code (e.g., +1 for US, +44 for UK)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {widgetConfig.enableSuggestions && (
              <div style={{ paddingTop: '3rem' }}>
                <h3 className="section-title">Quick Suggestions</h3>
                <div className="space-y-4">
                  {widgetConfig.suggestions.map((suggestion, index) => (
                    <div key={index} className="flex gap-3 items-center">
                      <input
                        type="text"
                        value={suggestion}
                        onChange={(e) => updateSuggestion(index, e.target.value)}
                        className="form-input flex-1"
                        placeholder="Enter suggestion..."
                      />
                      <button
                        onClick={() => removeSuggestion(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          padding: '0'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {widgetConfig.suggestions.length < 6 && (
                    <button className="btn-primary mt-4" onClick={addSuggestion}>
                      + Add Suggestion
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-gray-200 flex items-center justify-end gap-4">
              {!isSaved && !isSaving && (
                <span className="text-sm flex items-center gap-2 font-medium" style={{ color: '#f59e0b' }}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Unsaved changes
                </span>
              )}
              <button 
                className="btn-primary" 
                onClick={saveWidget}
                disabled={isSaving}
                style={{ opacity: isSaving ? 0.6 : 1 }}
              >
                {isSaving ? 'Saving...' : isSaved ? 'Saved ✓' : 'Save Widget'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetBuilder;